import { BedarfStatus } from './bedarf';

// Spiegelbilder der Prisma-Enums aus
// backend/src/modules/stellenausschreibungen/stellenausschreibungen.constants.ts.
// Die Regeln selbst stehen dort — hier wird nur dargestellt.

export enum AusschreibungKanal {
  WEBSITE = 'WEBSITE',
  DIAKONIE = 'DIAKONIE',
  AGENTUR_FUER_ARBEIT = 'AGENTUR_FUER_ARBEIT',
}

export enum StellenanzeigeStatus {
  VEROEFFENTLICHT = 'VEROEFFENTLICHT',
  DEAKTIVIERT = 'DEAKTIVIERT',
}

export enum AusschreibungSpalte {
  AUSZUSCHREIBEN = 'AUSZUSCHREIBEN',
  IN_ARBEIT = 'IN_ARBEIT',
  VEROEFFENTLICHT = 'VEROEFFENTLICHT',
  ZU_DEAKTIVIEREN = 'ZU_DEAKTIVIEREN',
  ERLEDIGT = 'ERLEDIGT',
}

export enum AusschreibungScope {
  BEDARF = 'BEDARF',
  ORT = 'ORT',
}

export const AUSSCHREIBUNG_KANAELE: AusschreibungKanal[] = [
  AusschreibungKanal.WEBSITE,
  AusschreibungKanal.DIAKONIE,
  AusschreibungKanal.AGENTUR_FUER_ARBEIT,
];

export const KanalConfig: Record<
  AusschreibungKanal,
  { label: string; kurz: string }
> = {
  [AusschreibungKanal.WEBSITE]: { label: 'Website', kurz: 'Web' },
  [AusschreibungKanal.DIAKONIE]: { label: 'Diakonie', kurz: 'Diak' },
  [AusschreibungKanal.AGENTUR_FUER_ARBEIT]: {
    label: 'Agentur für Arbeit',
    kurz: 'AfA',
  },
};

export const AUSSCHREIBUNG_SPALTEN_ORDER: AusschreibungSpalte[] = [
  AusschreibungSpalte.AUSZUSCHREIBEN,
  AusschreibungSpalte.IN_ARBEIT,
  AusschreibungSpalte.VEROEFFENTLICHT,
  AusschreibungSpalte.ZU_DEAKTIVIEREN,
  AusschreibungSpalte.ERLEDIGT,
];

export const AusschreibungSpalteConfig: Record<
  AusschreibungSpalte,
  {
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
    description: string;
  }
> = {
  [AusschreibungSpalte.AUSZUSCHREIBEN]: {
    label: 'Auszuschreiben',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    description:
      'Die Stelle ist zu besetzen, es steht aber noch auf keinem Kanal eine Anzeige.',
  },
  [AusschreibungSpalte.IN_ARBEIT]: {
    label: 'In Arbeit',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-200',
    description: 'Ein Teil der Kanäle ist bespielt, es fehlt noch mindestens einer.',
  },
  [AusschreibungSpalte.VEROEFFENTLICHT]: {
    label: 'Veröffentlicht',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    description: 'Die Anzeige steht auf allen drei Kanälen.',
  },
  [AusschreibungSpalte.ZU_DEAKTIVIEREN]: {
    label: 'Zu deaktivieren',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
    description:
      'Die Stelle wird nicht mehr besetzt, es steht aber noch mindestens eine Anzeige online.',
  },
  [AusschreibungSpalte.ERLEDIGT]: {
    label: 'Erledigt',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-200',
    description:
      'Alles abgeschaltet. Zeigt die letzten 30 Tage — danach verschwindet die Karte.',
  },
};

/** Spalten, in denen noch etwas zu tun ist — die Karte zeigt dort Checkboxen. */
export const AUSSCHREIBUNG_ARBEITS_SPALTEN: AusschreibungSpalte[] = [
  AusschreibungSpalte.AUSZUSCHREIBEN,
  AusschreibungSpalte.IN_ARBEIT,
  AusschreibungSpalte.ZU_DEAKTIVIEREN,
];

export interface UserRef {
  id: string;
  firstName: string;
  lastName: string;
}

export interface AusschreibungKanalState {
  kanal: AusschreibungKanal;
  /** `null` = für diesen Kanal gibt es keine Anzeige. */
  status: StellenanzeigeStatus | null;
  url: string | null;
  publishedAt: string | null;
  publishedBy: UserRef | null;
  deactivatedAt: string | null;
  deactivatedBy: UserRef | null;
}

export interface AusschreibungCard {
  refId: string;
  scope: AusschreibungScope;
  spalte: AusschreibungSpalte;
  liveCount: number;
  kanaele: AusschreibungKanalState[];
  ortName: string | null;
  bedarf: {
    childFirstName: string;
    childLastName: string | null;
    schoolName: string | null;
    status: BedarfStatus;
    wochenstunden: number | null;
    wochenstundenText: string | null;
  } | null;
  ort: { name: string; aktiveBedarfe: number } | null;
  letzteAenderungAt: string | null;
}

export type AusschreibungEventType = 'NEU_AKTIV' | 'WURDE_AKTIV' | 'WURDE_INAKTIV';

export interface AusschreibungEvent {
  id: string;
  bedarfId: string;
  eventType: AusschreibungEventType;
  occurredAt: string;
  user: UserRef | null;
  bedarf: {
    childFirstName: string;
    childLastName: string | null;
    schoolCityName: string | null;
    currentStatus: BedarfStatus | null;
  };
  statusChange?: { from: BedarfStatus; to: BedarfStatus };
  ausschreibung: { spalte: AusschreibungSpalte | null; liveCount: number };
}

export interface AusschreibungBoardMeta {
  scope: AusschreibungScope;
  counts: Record<AusschreibungSpalte, number>;
  /**
   * Ob das Backend Zugangsdaten für die Jobs-API der eigenen Website hat. Ohne
   * sie gibt es den Knopf „Veröffentlichen" nicht — ein Knopf, der nur scheitern
   * kann, ist schlimmer als keiner.
   */
  websitePublishVerfuegbar?: boolean;
}

/** Was beim Abhaken mit der Anzeige auf der eigenen Website geschehen ist. */
export type WebsiteAktion = 'ZURUECKGEZOGEN' | 'FEHLGESCHLAGEN';

/** Antwort auf einen Klick auf „Veröffentlichen". */
export interface WebsitePublishMeta {
  url: string;
  titel: string;
  aktion: 'angelegt' | 'aktualisiert';
  warnungen: string[];
}

/**
 * In welchen Spalten „Veröffentlichen" angeboten wird.
 *
 * In „Zu deaktivieren" steht die Stelle nicht mehr zur Besetzung — dort wäre der
 * Knopf die Umkehrung dessen, was die Spalte verlangt. In „Erledigt" ist die
 * Karte ohnehin nur noch Nachschlagewerk.
 */
export const AUSSCHREIBUNG_PUBLISH_SPALTEN: AusschreibungSpalte[] = [
  AusschreibungSpalte.AUSZUSCHREIBEN,
  AusschreibungSpalte.IN_ARBEIT,
];

export function formatCardTitle(card: AusschreibungCard): string {
  if (card.scope === AusschreibungScope.ORT) return card.ort?.name ?? 'Unbekannt';
  const { childFirstName, childLastName } = card.bedarf!;
  return childLastName ? `${childFirstName} ${childLastName}` : childFirstName;
}
